<template>
  <div>
    <div class="page-header">
      <div class="page-leftheader">
        <div class="headline">
          <h4 class="page-title text-primary">Neuigkeiten</h4>
          <p class="page-subtitle mb-0">
            Meldungen, die den Nutzern in der App angezeigt werden — jeweils nur
            denen, deren Sprache zur Neuigkeit passt.
          </p>
        </div>
      </div>
      <div class="page-rightheader">
        <a class="btn btn-primary btn-pill mr-1" @click="openEditNewsMessage(null, 'new')">
          <i class="fe fe-plus mr-1"></i>
          Neuigkeit hinzufügen
        </a>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          Alle Neuigkeiten
          <span class="badge badge-primary ml-2" v-if="newsMessages.length > 0">
            {{ newsMessages.length }}
          </span>
        </h3>
      </div>

      <!-- Ladehinweis, Leerzustand und Tabelle sauber getrennt: eine leere
           Tabelle ohne Erklärung wirkt sonst wie ein Fehler. -->
      <div class="card-body text-center text-muted" v-if="isLoading">
        Neuigkeiten werden geladen …
      </div>

      <div class="card-body text-center" v-else-if="newsMessages.length === 0">
        <i class="fe fe-inbox news-empty-icon"></i>
        <h4 class="mt-3">Noch keine Neuigkeiten</h4>
        <p class="text-muted">
          Neuigkeiten erscheinen in der App und eignen sich für Ankündigungen,
          Hinweise und Aktionen.
        </p>
        <a class="btn btn-primary btn-pill" @click="openEditNewsMessage(null, 'new')">
          Erste Neuigkeit anlegen
        </a>
      </div>

      <div class="table-responsive" v-else>
        <table class="table card-table table-vcenter">
          <thead>
            <tr>
              <th>Bild</th>
              <th>Sprache</th>
              <th>Beschreibung</th>
              <th>Verlinkung</th>
              <th>Angelegt</th>
              <th class="text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="newsMessage in newsMessages" v-bind:key="newsMessage._id">
              <td class="align-middle">
                <img
                  v-bind:src="`${$img(newsMessage.image, $awsLargeImages)}`"
                  alt=""
                  class="avatar avatar-md brround"
                  @error="handleImageError"
                />
              </td>
              <td class="align-middle">
                <img
                  height="16"
                  class="mr-2 align-middle"
                  v-bind:src="flagUrl(newsMessage.language)"
                  v-bind:alt="languageName(newsMessage.language)"
                />
                <span class="align-middle">{{ languageName(newsMessage.language) }}</span>
              </td>
              <td class="align-middle news-description" v-bind:title="newsMessage.description">
                {{ newsMessage.description }}
              </td>
              <td class="align-middle">
                <a
                  v-if="newsMessage.link"
                  v-bind:href="newsMessage.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="news-link"
                  v-bind:title="newsMessage.link"
                >
                  <i class="fe fe-external-link mr-1"></i>
                  {{ newsMessage.linkText || 'Link' }}
                </a>
                <span class="text-muted" v-else>—</span>
              </td>
              <td class="align-middle text-muted">{{ formatDate(newsMessage.createdAt) }}</td>
              <td class="align-middle text-right">
                <a
                  title="Neuigkeit bearbeiten"
                  class="btn btn-primary btn-small mr-1"
                  @click="openEditNewsMessage(newsMessage, 'edit')"
                  ><i class="fas fa-edit"></i
                ></a>
                <a
                  title="Neuigkeit löschen"
                  class="btn btn-small btn-danger"
                  @click="openDeleteNewsMessage(newsMessage)"
                  ><i class="fas fa-trash-alt"></i
                ></a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Anlegen und Bearbeiten -->
    <v-dialog v-model="editDialog" width="720" scrollable>
      <v-card>
        <v-card-title class="card">
          <h4 class="page-title text-primary">
            {{ isNew ? 'Neue Neuigkeit anlegen' : 'Neuigkeit bearbeiten' }}
          </h4>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-muted news-intro">
            Die Neuigkeit erscheint in der App bei allen Nutzern, deren
            Spracheinstellung der gewählten Sprache entspricht. Für mehrere
            Sprachen legen Sie je einen Eintrag an.
          </p>

          <div class="row">
            <!-- Eingaben -->
            <div class="col-md-7">
              <div class="form-group">
                <label class="form-label" for="newsDescription">
                  Beschreibung <span class="text-danger">*</span>
                </label>
                <textarea
                  id="newsDescription"
                  rows="4"
                  class="form-control"
                  placeholder="Was gibt es Neues?"
                  v-model="form.description"
                  v-bind:class="{ 'is-invalid': showValidation && !hasText(form.description) }"
                ></textarea>
                <div class="invalid-feedback d-block" v-if="showValidation && !hasText(form.description)">
                  Ohne Text lässt sich die Neuigkeit nicht speichern.
                </div>
                <small class="text-muted">Der Text, den die Nutzer lesen.</small>
              </div>

              <div class="form-group">
                <label class="form-label">
                  Sprache <span class="text-danger">*</span>
                </label>
                <!--
                  Eigene Kacheln statt Auswahlfeld: das Vuetify-Feld brachte seine
                  eigene Schriftfarbe mit und war im dunklen Dialog kaum zu lesen.
                -->
                <div class="language-picker">
                  <button
                    type="button"
                    class="language-option"
                    v-for="language in LANGUAGES"
                    v-bind:key="language.code"
                    v-bind:class="{ selected: form.language === language.code }"
                    @click="form.language = language.code"
                  >
                    <img height="14" v-bind:src="flagUrl(language.code)" alt="" />
                    <span>{{ language.name }}</span>
                  </button>
                </div>
                <small class="text-muted">
                  Nur Nutzer mit dieser Spracheinstellung bekommen die Neuigkeit zu sehen.
                </small>
              </div>

              <div class="form-group">
                <label class="form-label" for="newsLink">Verlinkung (freiwillig)</label>
                <input
                  id="newsLink"
                  type="text"
                  v-model="form.link"
                  class="form-control"
                  placeholder="https://enjoy-the-street.de/…"
                />
                <small class="text-muted">
                  Adresse, die sich beim Antippen öffnet. Leer lassen, wenn die
                  Neuigkeit nur informiert.
                </small>
              </div>

              <div class="form-group" v-if="hasText(form.link)">
                <label class="form-label" for="newsLinkText">Beschriftung der Schaltfläche</label>
                <input
                  id="newsLinkText"
                  type="text"
                  v-model="form.linkText"
                  class="form-control"
                  placeholder="Mehr erfahren"
                />
                <small class="text-muted">
                  Ohne Angabe steht auf der Schaltfläche „Mehr erfahren“.
                </small>
              </div>

              <div class="form-group mb-0">
                <label class="form-label">
                  Bild <span class="text-danger" v-if="isNew">*</span>
                </label>
                <div class="input-group file-browser">
                  <input
                    type="text"
                    id="imageFileDisplay"
                    class="form-control browse-file"
                    placeholder="Keine Datei gewählt"
                    disabled
                    v-bind:class="{ 'is-invalid': showValidation && isNew && !hasImage }"
                  />
                  <label class="input-group-text btn btn-primary mb-0">
                    Durchsuchen
                    <input
                      id="imageFile"
                      ref="imageFile"
                      type="file"
                      class="file-browserinput"
                      style="display: none"
                      accept="image/png, image/jpeg"
                      v-on:change="setFileName"
                    />
                  </label>
                </div>
                <!-- WAR FALSCH: Diese Meldung stand hier ohne Rücksicht auf
                     isNew, obwohl das Feld nur beim Neuanlegen ein
                     Pflichtfeld ist (Sternchen oben: v-if="isNew"). Wer eine
                     bestehende Neuigkeit ohne Bild öffnete, sah "Bitte ein
                     Bild auswählen" und konnte nicht einmal eine reine
                     Textkorrektur speichern. -->
                <div class="invalid-feedback d-block" v-if="showValidation && isNew && !hasImage">
                  Bitte ein Bild auswählen.
                </div>
                <small class="text-muted">JPG oder PNG, höchstens 20 MB.</small>
              </div>
            </div>

            <!-- Vorschau: zeigt vor dem Speichern, was in der App ankommt -->
            <div class="col-md-5">
              <label class="form-label">Vorschau</label>
              <div class="news-preview">
                <div class="news-preview-image">
                  <img v-if="previewImage" v-bind:src="previewImage" alt="" />
                  <div class="news-preview-placeholder" v-else>
                    <i class="fe fe-image"></i>
                    <span>Noch kein Bild</span>
                  </div>
                </div>
                <div class="news-preview-body">
                  <p class="news-preview-text">
                    {{ form.description || 'Hier steht der Text der Neuigkeit.' }}
                  </p>
                  <span class="news-preview-button" v-if="hasText(form.link)">
                    {{ form.linkText || 'Mehr erfahren' }}
                  </span>
                </div>
              </div>
              <small class="text-muted d-block mt-2">
                So erscheint die Neuigkeit ungefähr in der App.
              </small>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <a class="btn btn-secondary btn-pill mr-1" @click="editDialog = false">Abbrechen</a>
          <a
            class="btn btn-primary btn-pill"
            v-bind:class="{ 'is-locked': isSaving }"
            @click="saveNewsMessage()"
          >
            {{ isSaving ? 'Wird gespeichert …' : 'Speichern' }}
          </a>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Löschen -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card>
        <v-card-title class="card">
          <h4 class="page-title text-primary">Neuigkeit löschen</h4>
        </v-card-title>
        <v-card-text>
          Soll diese Neuigkeit wirklich gelöscht werden? Sie wird dann nicht mehr
          in der App angezeigt.
          <!-- Ohne den Text weiß niemand, welchen Eintrag er gerade löscht -->
          <div class="news-delete-hint mt-3" v-if="deleteTarget">
            <img
              v-bind:src="`${$img(deleteTarget.image, $awsLargeImages)}`"
              alt=""
              class="avatar avatar-md brround mr-3"
              @error="handleImageError"
            />
            <div>
              <div>{{ deleteTarget.description }}</div>
              <small class="text-muted">
                {{ languageName(deleteTarget.language) }} &middot;
                angelegt {{ formatDate(deleteTarget.createdAt) }}
              </small>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <a class="btn btn-secondary btn-pill mr-1" @click="deleteDialog = false">Abbrechen</a>
          <a
            class="btn btn-danger btn-pill"
            v-bind:class="{ 'is-locked': isDeleting }"
            @click="deleteNewsMessage()"
            >Endgültig löschen</a
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Klarnamen statt Kürzel — „dk“ sagt niemandem etwas.
const LANGUAGES = [
  { code: 'de', name: 'Deutsch' },
  { code: 'en', name: 'Englisch' },
  { code: 'pl', name: 'Polnisch' },
  { code: 'it', name: 'Italienisch' },
  { code: 'fr', name: 'Französisch' },
  { code: 'da', name: 'Dänisch' },
  { code: 'es', name: 'Spanisch' },
];

const MAX_FILE_SIZE_MB = 20;

export default {
  components: {},

  data() {
    return {
      LANGUAGES,

      newsMessages: [],
      // Arbeitskopie: früher wurde das Listenobjekt selbst bearbeitet, wodurch
      // die Tabelle schon während des Tippens mitlief und „Abbrechen“ nichts verwarf.
      form: this.emptyForm(),
      editType: 'new',
      deleteTarget: null,
      selectedFile: null,
      updatedPic: '',

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showValidation: false,

      editDialog: false,
      deleteDialog: false,
    };
  },

  computed: {
    isNew() {
      return this.editType === 'new';
    },

    // Beim Anlegen muss ein Bild dabei sein, beim Bearbeiten reicht das vorhandene.
    hasImage() {
      return this.selectedFile != null || this.hasText(this.form.image);
    },

    previewImage() {
      if (this.updatedPic) return this.updatedPic;
      if (this.hasText(this.form.image)) return this.$img(this.form.image, this.$awsLargeImages);
      return null;
    },

    isValid() {
      // WAR FALSCH: Ein Bild wurde IMMER verlangt, auch beim Bearbeiten einer
      // bestehenden Neuigkeit ohne Bild — obwohl die Beschriftung das rote
      // Pflicht-Sternchen nur beim Neuanlegen zeigt (v-if="isNew" weiter
      // unten in der Vorlage). Formular und Prüfung widersprachen sich: Es
      // gibt Neuigkeiten ohne Bild (der Server legt sie auch ohne Datei an),
      // und die ließen sich damit nie wieder speichern, selbst für eine
      // reine Textkorrektur. Beim Bearbeiten bleibt ein fehlendes Bild jetzt
      // erlaubt.
      const imageOk = this.isNew ? this.hasImage : true;
      return this.hasText(this.form.description) && this.hasText(this.form.language) && imageOk;
    },
  },

  watch: {
    editDialog(isOpen) {
      if (!isOpen) {
        this.showValidation = false;
      }
    },
  },

  mounted() {
    this.fetchNewsMessages();
    if (this.$route.params.newsMessage) {
      this.openEditNewsMessage(this.$route.params.newsMessage, 'edit');
    }
    this.$root.$on('NewsMessages', this.onNewsMessageEvent);
  },

  beforeDestroy() {
    // Ohne Abmeldung sammelt sich bei jedem Seitenwechsel ein weiterer Zuhörer an
    this.$root.$off('NewsMessages', this.onNewsMessageEvent);
  },

  methods: {
    emptyForm() {
      return {
        _id: '',
        description: '',
        image: '',
        link: '',
        linkText: '',
        language: 'de',
      };
    },

    hasText(value) {
      return value != null && String(value).trim().length > 0;
    },

    languageName(code) {
      const language = LANGUAGES.find((entry) => entry.code === code);
      return language ? language.name : 'Unbekannt';
    },

    // Fängt eine kaputte Bildadresse ab (Datei im Bildspeicher gelöscht,
    // Upload nur halb durchgelaufen). Ohne diesen Rückfall zeigte der
    // Browser nur das zerbrochene Bildsymbol.
    // WAR FALSCH (zweimal): Zuerst setzte die Bremse "event.target.onerror =
    // null" — das löscht nur die alte HTML-Eigenschaft onerror, die hier nie
    // benutzt wird. Die Vorlage meldet den Rückfall über "@error", Vue
    // registriert das intern per addEventListener; dieser Zuhörer blieb
    // aktiv, setzte bei einem scheiternden Ersatzbild erneut dieselbe
    // Adresse und drehte sich damit endlos im Kreis. Der zweite Anlauf
    // merkte sich den Rückfall dauerhaft am Bild-Element selbst
    // ("data-fallback-used"). Das bremste zwar die Schleife, galt aber für
    // IMMER: Die Tabellenzeilen haben einen festen Schlüssel (:key), Vue
    // verwendet dasselbe Bild-Element über jedes Neuladen der Liste hinweg
    // weiter. Lud der Verwalter danach ein neues, ebenfalls kaputtes Bild
    // hoch, bekam diese Zeile keinen Rückfall mehr und zeigte wieder das
    // zerbrochene Bildsymbol des Browsers. Jetzt wird nicht mehr gemerkt
    // "Rückfall schon einmal benutzt", sondern geprüft, ob die AKTUELLE
    // Adresse bereits das Ersatzbild ist — das bremst genau die echte
    // Schleife (das Ersatzbild scheitert selbst) und lässt jedes neue Bild
    // wieder zu.
    handleImageError(event) {
      const fallbackImage = '/images/brand/favicon.png';
      if (event.target.getAttribute('src') === fallbackImage) return;
      event.target.src = fallbackImage;
    },

    flagUrl(code) {
      // Für Englisch liegt die Flagge unter „us“; ohne Sprache gäbe es sonst
      // einen Verweis auf undefined.svg und ein kaputtes Bild.
      if (!code) return '/images/brand/favicon.png';
      return `/images/flags/${code === 'en' ? 'us' : code}.svg`;
    },

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    onNewsMessageEvent(newsMessage) {
      this.openEditNewsMessage(newsMessage, 'edit');
    },

    fetchNewsMessages() {
      const _this = this;
      this.isLoading = true;
      this.$axios
        .get('/newsmessage/all')
        .then(function (response) {
          // WAR FALSCH: Der Server liefert die Neuigkeiten ohne Sortiervorgabe
          // (keine Ordnung nach Anlegedatum) — in der Tabelle sprang die
          // Spalte "Angelegt" wild zwischen den Daten hin und her, die
          // zuletzt angelegte Neuigkeit stand nicht oben. Hier wird zumindest
          // die Dashboard-Ansicht nach Anlegedatum sortiert (neueste zuerst);
          // eine Kopie, damit die ursprüngliche Serverreihenfolge nicht
          // verändert wird.
          const data = (response && response.data && response.data.data) || [];
          _this.newsMessages = data.slice().sort(function (a, b) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          _this.isLoading = false;
        })
        .catch(function (error) {
          _this.isLoading = false;
          _this.$noty.error('Die Neuigkeiten konnten nicht geladen werden.');
          console.error(error);
        });
    },

    openEditNewsMessage(newsMessage, type) {
      this.editType = type;
      this.showValidation = false;
      this.selectedFile = null;
      this.updatedPic = '';

      if (type === 'edit' && newsMessage) {
        this.form = Object.assign(this.emptyForm(), newsMessage);
      } else {
        this.form = this.emptyForm();
      }

      this.resetFileInput();
      this.editDialog = true;
    },

    openDeleteNewsMessage(newsMessage) {
      this.deleteTarget = newsMessage;
      this.deleteDialog = true;
    },

    deleteNewsMessage() {
      if (this.isDeleting || !this.deleteTarget) return;

      const _this = this;
      this.isDeleting = true;
      this.$axios
        .delete('/newsmessage/delete/' + this.deleteTarget._id)
        .then(function (response) {
          _this.isDeleting = false;
          // WAR FALSCH: Gleicher Fall wie beim Speichern — die Antwort wurde
          // nicht auf "success" geprüft. Bei einer bereits gelöschten oder
          // nicht mehr vorhandenen Neuigkeit (zweiter Klick, zweiter
          // Verwalter, alter Reiter) antwortet das Backend mit
          // success:false, aber weiterhin Statuscode 200 — die grüne
          // Erfolgsmeldung erschien trotzdem, obwohl nichts gelöscht wurde.
          if (response && response.data && response.data.success === false) {
            // WAR FALSCH: Hier stand serverMessage({ response }, '...') — die
            // Hilfsfunktion bevorzugt IMMER den vom Server mitgeschickten
            // Text und benutzt den deutschen Ersatztext nur, wenn gar keiner
            // mitkommt. Dieser Server-Teil antwortet aber englisch (z.B.
            // "NewsMessage not found"), also gewann hier immer die englische
            // Meldung und erschien mitten in der deutschen Oberfläche. Jetzt
            // fest der deutsche Text; die englische Serverantwort steht
            // weiterhin in der Konsole (siehe console.error direkt darunter).
            console.error(response.data.message);
            _this.$noty.error('Die Neuigkeit konnte nicht gelöscht werden.');
            return;
          }
          _this.$noty.success('Die Neuigkeit wurde gelöscht.');
          _this.deleteDialog = false;
          _this.deleteTarget = null;
          _this.fetchNewsMessages();
        })
        .catch(function (error) {
          _this.isDeleting = false;
          _this.$noty.error(_this.serverMessage(error, 'Die Neuigkeit konnte nicht gelöscht werden.'));
          console.error(error);
        });
    },

    saveNewsMessage() {
      if (this.isSaving) return;

      this.showValidation = true;
      if (!this.isValid) {
        this.$noty.error('Bitte die rot markierten Felder ausfüllen.');
        return;
      }
      this.saveNewsMessageToServer();
    },

    saveNewsMessageToServer() {
      const _this = this;
      const isNew = this.isNew;
      const newsMessageId = this.form._id;
      const payload = Object.assign({}, this.form);

      // Beim Hochladen ersetzt die Datei das Feld; sonst schickt der Browser
      // Datei und alten Pfad gleichzeitig.
      if (this.selectedFile) {
        delete payload.image;
      }
      delete payload._id;

      const body = this.$formatUrlEncoded(payload, this.selectedFile ? [this.selectedFile] : []);
      const request = isNew
        ? this.$axios.post('/newsmessage', body)
        : this.$axios.put('/newsmessage/' + newsMessageId, body);

      this.isSaving = true;
      request
        .then(function (response) {
          _this.isSaving = false;
          // WAR FALSCH: Die Antwort wurde nicht auf "success" geprüft. Dieses
          // Backend antwortet bei einem fachlichen Fehlschlag (z.B. die
          // Neuigkeit wurde inzwischen von einem anderen Verwalter gelöscht)
          // trotzdem mit Statuscode 200 und schreibt das Ergebnis nur in
          // success/message. Ohne diese Prüfung erschien "gespeichert",
          // obwohl nichts passiert war — das Feld "success" kam in dieser
          // Datei bisher an keiner einzigen Stelle vor. Gleiches Muster wie
          // saveEventToServer() in Events.vue.
          if (response && response.data && response.data.success === false) {
            // WAR FALSCH: Gleicher Fall wie bei deleteNewsMessage() —
            // serverMessage() bevorzugt IMMER den englischen Text des
            // Servers, der deutsche Ersatztext kam nie zum Zug. Jetzt fest
            // der deutsche Text; die englische Serverantwort steht
            // weiterhin in der Konsole (siehe console.error direkt darunter).
            console.error(response.data.message);
            _this.$noty.error('Die Neuigkeit konnte nicht gespeichert werden.');
            return;
          }
          _this.$noty.success(
            isNew ? 'Die Neuigkeit wurde angelegt.' : 'Die Änderungen wurden gespeichert.',
          );
          _this.editDialog = false;
          _this.fetchNewsMessages();
        })
        .catch(function (error) {
          _this.isSaving = false;
          _this.$noty.error(_this.serverMessage(error, 'Die Neuigkeit konnte nicht gespeichert werden.'));
          console.error(error);
        });
    },

    serverMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return fallback;
    },

    setFileName() {
      const input = this.$refs.imageFile;
      const display = document.getElementById('imageFileDisplay');

      if (!input || input.files.length === 0) {
        this.selectedFile = null;
        this.updatedPic = '';
        if (display) display.value = '';
        return;
      }

      const file = input.files[0];
      if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
        this.$showErrorFileSize(MAX_FILE_SIZE_MB);
        this.resetFileInput();
        return;
      }

      this.selectedFile = file;
      this.updatedPic = URL.createObjectURL(file);
      if (display) display.value = file.name;
    },

    resetFileInput() {
      const input = this.$refs.imageFile;
      const display = document.getElementById('imageFileDisplay');
      if (input) input.value = '';
      if (display) display.value = '';
      this.selectedFile = null;
    },
  },
};
</script>

<style scoped>
.v-dialog > .v-card > .v-card__actions {
  padding: 8px 16px;
}

.v-select__selections {
  color: white !important;
}

/* Gestaltung des Erklärtexts: siehe `views/App.vue`, dort einmal zentral. */

.news-intro {
  font-size: 13px;
}

.news-empty-icon {
  font-size: 40px;
  color: #b4bdce;
}

/* Lange Beschreibungen sprengen sonst die Tabellenbreite */
.news-description {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-link {
  max-width: 180px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* Sprachauswahl als Kacheln — eigene Farben, damit sie in jedem Erscheinungsbild lesbar bleibt */
.language-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.language-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;
}

.language-option:hover {
  border-color: #467fcf;
}

.language-option.selected {
  background: #467fcf;
  border-color: #467fcf;
  color: #fff;
}

.news-delete-hint {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 10px;
  background: rgba(148, 163, 184, 0.12);
}

/* Nachbau der Darstellung in der App */
.news-preview {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  overflow: hidden;
}

.news-preview-image {
  height: 130px;
  background: rgba(148, 163, 184, 0.15);
}

.news-preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-preview-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8f9bb3;
  font-size: 12px;
}

.news-preview-placeholder i {
  font-size: 22px;
  margin-bottom: 4px;
}

.news-preview-body {
  padding: 12px;
}

.news-preview-text {
  font-size: 13px;
  margin-bottom: 10px;
  word-break: break-word;
}

.news-preview-button {
  display: inline-block;
  padding: 5px 14px;
  border-radius: 999px;
  background: #467fcf;
  color: #fff;
  font-size: 12px;
}

.btn.is-locked {
  pointer-events: none;
  opacity: 0.55;
}
</style>
